// ABOUTME: A month of day buttons under a weekday header, shared by the single-date and the range pickers;
// ABOUTME: each picker says which days are disabled or highlighted, and the grid draws every day the same way.

import type { FC } from "react"
import { isSameDay } from "date-fns"
import { monthGridDays } from "./calendarMonthDays"

const dayClass = (disabled: boolean, isCurrentMonth: boolean, highlighted: boolean) =>
  [
    "relative w-9 h-9 text-sm flex items-center justify-center transition-colors rounded-full",
    disabled ? "text-gray-300 cursor-not-allowed" : "cursor-pointer",
    !isCurrentMonth && !disabled ? "text-gray-400" : "",
    isCurrentMonth && !disabled && !highlighted ? "hover:bg-blue-50" : "",
  ].join(" ")

const circleClass = (highlighted: boolean, isToday: boolean) =>
  [
    "relative z-10 w-8 h-8 flex items-center justify-center rounded-full",
    highlighted ? "bg-blue-500 text-white" : "",
    isToday && !highlighted ? "border border-blue-500" : "",
  ].join(" ")

interface DayProps {
  date: Date
  isCurrentMonth: boolean
  disabled: boolean
  highlighted: boolean
  isToday: boolean
  onClick: (date: Date) => void
  onHover?: (date: Date | null) => void
}

const CalendarDay: FC<DayProps> = ({ date, isCurrentMonth, disabled, highlighted, isToday, onClick, onHover }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => !disabled && onClick(date)}
    onMouseEnter={onHover ? () => !disabled && onHover(date) : undefined}
    onMouseLeave={onHover ? () => onHover(null) : undefined}
    className={dayClass(disabled, isCurrentMonth, highlighted)}
  >
    <span className={circleClass(highlighted, isToday)}>{date.getDate()}</span>
  </button>
)

interface IProps {
  month: Date
  weekdays: string[]
  /** Per day: can it be picked, and is it drawn as chosen (the selected day, an end of the range). */
  dayState: (date: Date) => { disabled: boolean; highlighted: boolean }
  onDateClick: (date: Date) => void
  /** The range picker follows the pointer across the days. */
  onDateHover?: (date: Date | null) => void
}

export const CalendarMonthGrid: FC<IProps> = ({ month, weekdays, dayState, onDateClick, onDateHover }) => {
  const today = new Date()
  return (
    <div className="select-none">
      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-1">
        {weekdays.map((day) => (
          <div
            key={day}
            className="w-9 h-8 flex items-center justify-center text-xs text-gray-500 font-medium"
          >
            {day}
          </div>
        ))}
      </div>
      {/* Days grid */}
      <div className="grid grid-cols-7">
        {monthGridDays(month).map(({ date, isCurrentMonth }) => (
          <CalendarDay
            key={date.toISOString()}
            date={date}
            isCurrentMonth={isCurrentMonth}
            {...dayState(date)}
            isToday={isSameDay(date, today)}
            onClick={onDateClick}
            onHover={onDateHover}
          />
        ))}
      </div>
    </div>
  )
}
